"use client";

import { signIn, getProviders } from "next-auth/react";
import { useMemo, useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import {
  ErrorBoundaryWrapper,
  withErrorBoundary,
} from "@/components/ErrorBoundaryWrapper";
import { useLocale } from "next-intl";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useTranslations } from "next-intl";
import Link from "next/link";
import CaptchaBox from "@/components/CaptchaBox";
import { Alert, AlertDescription } from "@/components/ui/alert";

// 超级简化版登录页面，使用最基本的 HTML 元素
function SignInPage() {
  const t = useTranslations("auth");
  const tHttp = useTranslations("error.http");
  const router = useRouter();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const reason = searchParams?.get("reason") || null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [providers, setProviders] = useState<any>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [resendCaptchaToken, setResendCaptchaToken] = useState<string | null>(
    null,
  );
  const [resendCaptchaExpired, setResendCaptchaExpired] =
    useState<boolean>(false);
  const [resendCaptchaNonce, setResendCaptchaNonce] = useState<number>(0);
  const [resendCaptchaTimeoutHint, setResendCaptchaTimeoutHint] =
    useState<string>("");
  const [resendCaptchaRetryDone, setResendCaptchaRetryDone] =
    useState<boolean>(false);
  const OAUTH_CHALLENGE =
    (process.env.NEXT_PUBLIC_OAUTH_CHALLENGE || "on") === "on";
  const captchaDisabledByEnv =
    process.env.NEXT_PUBLIC_CAPTCHA_DISABLED === "true";
  const TURNSTILE_SITE_KEY = captchaDisabledByEnv
    ? ""
    : process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
  const HCAPTCHA_SITE_KEY = captchaDisabledByEnv
    ? ""
    : process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY || "";
  const CAPTCHA_PROVIDER: "turnstile" | "hcaptcha" | null = TURNSTILE_SITE_KEY
    ? "turnstile"
    : HCAPTCHA_SITE_KEY
      ? "hcaptcha"
      : null;
  const shouldRenderCaptcha = !!CAPTCHA_PROVIDER;

  const [effectiveCallbackUrl, setEffectiveCallbackUrl] = useState<
    string | null
  >(null);
  const [effectiveReason, setEffectiveReason] = useState<string | null>(reason);

  useEffect(() => {
    const raw = searchParams?.get("callbackUrl") || null;
    let safe: string | null = null;

    if (raw) {
      try {
        const decoded = decodeURIComponent(raw);
        if (decoded.startsWith("/") && !decoded.startsWith("//")) {
          safe = decoded;
        }
      } catch {
        if (raw.startsWith("/") && !raw.startsWith("//")) {
          safe = raw;
        }
      }
    }

    let resolvedReason: string | null = reason;
    const storedReason = (() => {
      try {
        return localStorage.getItem("tm_auth_reason");
      } catch {
        return null;
      }
    })();

    const allowStoredFallback =
      reason === "auth_required" || storedReason === "auth_required";

    if (!safe && allowStoredFallback) {
      try {
        const stored = localStorage.getItem("tm_auth_callbackUrl");
        const storedTs = Number(
          localStorage.getItem("tm_auth_callbackUrl_ts") || "0",
        );
        const isFresh =
          storedTs > 0 && Date.now() - storedTs < 24 * 60 * 60 * 1000;
        if (
          isFresh &&
          stored &&
          stored.startsWith("/") &&
          !stored.startsWith("//")
        )
          safe = stored;
        if (!resolvedReason && isFresh && storedReason)
          resolvedReason = storedReason;
      } catch {
        // ignore
      }
    }

    if (safe) {
      try {
        localStorage.setItem("tm_auth_callbackUrl", safe);
        localStorage.setItem("tm_auth_callbackUrl_ts", String(Date.now()));
        if (resolvedReason)
          localStorage.setItem("tm_auth_reason", resolvedReason);
      } catch {
        // ignore
      }
    }

    setEffectiveCallbackUrl(safe);
    setEffectiveReason(resolvedReason);
  }, [searchParams, reason]);

  const signupHref = useMemo(() => {
    const qs = new URLSearchParams();
    if (effectiveCallbackUrl) qs.set("callbackUrl", effectiveCallbackUrl);
    if (effectiveReason) qs.set("reason", effectiveReason);
    const s = qs.toString();
    return `/${locale}/auth/signup${s ? `?${s}` : ""}`;
  }, [effectiveCallbackUrl, effectiveReason, locale]);

  // 页面加载标记
  useEffect(() => {
    console.log("登录页面已加载");
    document.body.setAttribute("data-page-status", "signin-loaded");
    document.body.setAttribute("data-page-loaded", "true");
    document.body.setAttribute("data-page-name", "signin");

    // 可选：仍可获取以显示数量信息，但入口跳往前置挑战页
    getProviders().then((p) => setProviders(p || null));

    return () => {
      document.body.removeAttribute("data-page-status");
      document.body.removeAttribute("data-page-loaded");
      document.body.removeAttribute("data-page-name");
    };
  }, []);

  // Inline captcha loading timeout auto-remount (one-shot)
  useEffect(() => {
    if (!CAPTCHA_PROVIDER) return;
    if (resendCaptchaToken) return;
    if (resendCaptchaRetryDone) return;
    const timer = setTimeout(() => {
      setResendCaptchaTimeoutHint(t("login.captchaLoadingTimeout"));
      setResendCaptchaNonce((n) => n + 1);
      setResendCaptchaRetryDone(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [CAPTCHA_PROVIDER, resendCaptchaToken, resendCaptchaRetryDone, t]);

  // 错误信息规范化（组件作用域内）
  function normalizeError(err: string): string {
    if (!err) return "";
    const text = String(err);

    // 特殊处理：后端抛出的“邮箱或密码不正确，还剩 X 次尝试机会”中文文案，转为各语言本地化
    const remainingMatch = /邮箱或密码不正确，还剩\s*(\d+)\s*次尝试机会/.exec(
      text,
    );
    if (remainingMatch) {
      const remaining = parseInt(remainingMatch[1], 10);
      if (!Number.isNaN(remaining)) {
        return t("authErrors.invalidCredentialsRemaining", {
          count: remaining,
        });
      }
      return t("authErrors.invalidCredentials");
    }

    if (/CallbackRouteError/i.test(text) || /Configuration/i.test(text))
      return t("authErrors.configError");
    if (/CredentialsSignin/i.test(text))
      return t("authErrors.invalidCredentials");
    if (/用户不存在|user.?not.?found/i.test(text))
      return t("authErrors.userNotFound");
    if (/邮箱未验证|email.?not.?verified/i.test(text))
      return t("login.emailNotVerified");
    if (
      /账户已锁定|登录尝试次数过多|too.?many.*attempt|account.?locked/i.test(
        text,
      )
    )
      return t("authErrors.tooManyRequests");
    if (/无权访问该租户|tenant.?no.?access/i.test(text))
      return t("authErrors.tenantNoAccess");
    return t("authErrors.invalidCredentials");
  }

  // 处理社交登录
  const handleSocialSignIn = (providerId: string) => {
    setIsLoading(true);
    signIn(
      providerId,
      effectiveCallbackUrl ? { callbackUrl: effectiveCallbackUrl } : undefined,
    );
  };

  // 超级简化的提交处理函数
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMessage("");

    // 客户端表单验证（使用 i18n 文案，避免浏览器内置英文气泡）
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage(t("login.errors.emailRequired"));
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      setErrorMessage(t("login.errors.invalidEmail"));
      return;
    }

    if (!password) {
      setErrorMessage(t("login.errors.passwordRequired"));
      return;
    }

    setIsLoading(true);

    try {
      console.log("尝试登录:", email);

      // 使用最简单的登录调用，不传递任何额外参数
      const result = await signIn("credentials", {
        email,
        password,
        captchaToken,
        redirect: false,
      });

      console.log("登录结果:", result);

      if (result?.error) {
        setErrorMessage(normalizeError(result.error));
      } else {
        // 登录成功后，强制跳转到 Dashboard
        // 忽略 callbackUrl，确保所有用户登录后都进入 Dashboard
        const safeTarget = `/${locale}/dashboard`;

        try {
          localStorage.removeItem("tm_auth_callbackUrl");
          localStorage.removeItem("tm_auth_callbackUrl_ts");
          localStorage.removeItem("tm_auth_reason");
        } catch {
          // ignore
        }

        window.location.href = safeTarget;
      }
    } catch (error) {
      console.error("登录错误:", error);
      setErrorMessage("登录失败，请稍后再试");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ErrorBoundaryWrapper>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { href: "/auth", name: t("breadcrumb.auth") },
            { href: "/auth/signin", name: t("breadcrumb.signin") },
          ]}
        />
      </div>

      <div
        className="flex min-h-[calc(100vh-200px)] flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-rose-50 to-amber-50 dark:from-slate-900 dark:via-indigo-950/60 dark:to-amber-900/40 transition-colors"
        data-testid="signin-page"
      >
        <div className="w-full max-w-md space-y-8 rounded-3xl border border-border/60 bg-card/95 p-8 shadow-xl backdrop-blur-sm dark:border-border/40">
          {/* OAuth Providers - 暂时隐藏，待 Android app 上架后实现 */}
          {/* <div className="space-y-3">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-border/40" />
              <span className="mx-2 text-xs text-muted-foreground">
                {t("login.continueWith")}
              </span>
              <div className="flex-grow border-t border-border/40" />
            </div>
            {OAUTH_CHALLENGE ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link
                  href={`/${locale}/auth/oauth-challenge`}
                  className="col-span-1 sm:col-span-2"
                >
                  <div className="w-full text-center px-3 py-2 rounded-md border text-sm bg-white hover:bg-muted dark:bg-white/5 dark:hover:bg-white/10 dark:text-white">
                    {t("login.continueWith")}
                  </div>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.values(providers || {})
                  .filter((p: any) => p.id !== "credentials")
                  .map((p: any) => {
                    const providerLabels: Record<string, string> = {
                      google: t("login.google"),
                      github: t("login.github"),
                      wechat: t("login.wechat"),
                      facebook: t("login.facebook"),
                      twitter: t("login.twitter"),
                      linkedin: t("login.linkedin"),
                      discord: t("login.discord"),
                      reddit: t("login.reddit"),
                      vk: t("login.vk"),
                      line: t("login.line"),
                      kakao: t("login.kakao"),
                    };
                    const label =
                      providerLabels[p.id] ||
                      `${t("login.continueWith")} ${p.name}`;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="px-3 py-2 rounded-md border text-sm bg-white hover:bg-muted dark:bg-white/5 dark:hover:bg-white/10 dark:text-white"
                        onClick={() => signIn(p.id)}
                      >
                        {label}
                      </button>
                    );
                  })}
              </div>
            )}
          </div> */}

          {/* Login Form */}
          <form
            onSubmit={handleSubmit}
            noValidate
            data-testid="signin-form"
            className="space-y-6"
          >
            {effectiveReason === "auth_required" && !errorMessage && (
              <Alert className="mb-2">
                <AlertDescription>{tHttp("401_description")}</AlertDescription>
              </Alert>
            )}
            {errorMessage && (
              <Alert className="mb-2">
                <AlertDescription>
                  {/邮箱未验证/i.test(errorMessage)
                    ? t("login.emailNotVerified")
                    : errorMessage}
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("login.email")}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                  className="w-full px-3 py-2 border border-input bg-background/95 rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-muted/30 dark:border-border/50 dark:text-foreground"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("login.password")}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                    className="w-full px-3 py-2 pr-10 border border-input bg-background/95 rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-muted/30 dark:border-border/50 dark:text-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={
                      showPassword
                        ? t("login.hidePassword")
                        : t("login.showPassword")
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                href={`/${locale}/auth/forgot-password`}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              data-testid="signin-button"
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-foreground bg-gradient-to-r from-primary/70 via-rose-300/70 to-amber-200/70 hover:from-primary/80 hover:via-rose-300/80 hover:to-amber-200/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:from-primary/60 dark:via-rose-400/60 dark:to-amber-200/60"
            >
              {isLoading ? t("login.submit") : t("login.submit")}
            </button>
            {shouldRenderCaptcha && (
              <div className="mt-2">
                <CaptchaBox onVerify={(t) => setCaptchaToken(t)} />
              </div>
            )}

            {/* Register Link */}
            <div className="text-center">
              <span className="text-sm text-muted-foreground">
                {t("login.noAccount")}{" "}
              </span>
              <Link
                href={signupHref}
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                {t("login.signUp")}
              </Link>
            </div>

            {/* Inline resend verification when email is not verified */}
            {/邮箱未验证/i.test(errorMessage) && (
              <div className="mt-4 p-3 rounded-md border bg-muted/20">
                <div className="text-sm mb-2">
                  {t("login.resendInlinePrompt")}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <input
                      type="email"
                      value={resendEmail || email}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-3 py-2 border rounded-md bg-background/95"
                    />
                  </div>
                  {shouldRenderCaptcha && (
                    <div
                      className="mt-1"
                      aria-busy={
                        !!CAPTCHA_PROVIDER && !resendCaptchaToken
                          ? true
                          : undefined
                      }
                    >
                      <CaptchaBox
                        key={`resend-${resendCaptchaNonce}`}
                        onVerify={(t) => {
                          setResendCaptchaToken(t);
                          if (t) setResendCaptchaExpired(false);
                        }}
                        onExpired={() => setResendCaptchaExpired(true)}
                      />
                    </div>
                  )}
                  <div>
                    <button
                      type="button"
                      disabled={
                        resending ||
                        !(resendEmail || email) ||
                        (!!CAPTCHA_PROVIDER && !resendCaptchaToken)
                      }
                      onClick={async () => {
                        const targetEmail = (resendEmail || email).trim();
                        if (!targetEmail) return;
                        setResendMsg("");
                        setResending(true);
                        try {
                          const resp = await fetch(
                            "/api/auth/send-verification-public",
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                email: targetEmail,
                                captchaToken: resendCaptchaToken,
                                captchaProvider: CAPTCHA_PROVIDER || undefined,
                                locale,
                              }),
                            },
                          );
                          const j = await resp.json().catch(() => ({}));
                          if (resp.ok) {
                            setResendMsg(t("register.verificationSent"));
                          } else {
                            const raw = String(j?.message || "");
                            if (/验证码校验失败|captcha/i.test(raw)) {
                              setResendMsg(t("login.captchaFailed"));
                            } else {
                              setResendMsg(t("register.sendFailed"));
                            }
                          }
                        } catch {
                          setResendMsg(t("register.sendFailed"));
                        } finally {
                          setResending(false);
                          // 强制刷新验证码：清空 token 并重挂载控件，避免等待第三方过期刷新
                          setResendCaptchaToken(null);
                          setResendCaptchaNonce((n) => n + 1);
                          setResendCaptchaExpired(false);
                          setResendCaptchaTimeoutHint("");
                        }
                      }}
                      className="px-3 py-2 rounded-md border text-sm"
                    >
                      {resending
                        ? t("register.sending")
                        : t("login.resendVerification")}
                    </button>
                  </div>
                  {!!CAPTCHA_PROVIDER &&
                    !resendCaptchaToken &&
                    !resendCaptchaExpired &&
                    !resendCaptchaTimeoutHint && (
                      <div
                        className="text-xs text-muted-foreground"
                        aria-live="polite"
                      >
                        {t("register.getCaptcha") || ""}
                      </div>
                    )}
                  {!!CAPTCHA_PROVIDER &&
                    !resendCaptchaToken &&
                    !!resendCaptchaTimeoutHint && (
                      <div
                        className="text-xs text-muted-foreground"
                        aria-live="polite"
                      >
                        {resendCaptchaTimeoutHint}
                      </div>
                    )}
                  {!!CAPTCHA_PROVIDER && resendCaptchaExpired && (
                    <div
                      className="text-xs text-muted-foreground"
                      aria-live="polite"
                    >
                      {t("login.captchaExpired") || ""}
                    </div>
                  )}
                  {resendMsg && (
                    <div
                      className="text-xs text-muted-foreground"
                      aria-live="polite"
                    >
                      {resendMsg}
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </ErrorBoundaryWrapper>
  );
}

// 使用高阶组件包装页面组件
export default withErrorBoundary(SignInPage);
